/**
 * mSTEM Robot Manager Modal (GEN-03). Presentational component — Redux
 * wiring lives in src/containers/robot-manager-modal.jsx.
 *
 * Two render branches:
 *   - inEditMode (isCreating OR editingRobot) → RobotEditForm + form title
 *   - else → toolbar (+ Add Robot button) + MstemTable of 5 mock robots
 *
 * Status badge variant mapping: ready→success, outdated→warning, beta→info.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';
import RobotActionsCell from './robot-actions-cell.jsx';
import RobotEditForm from './robot-edit-form.jsx';
import DeviceConnectionModal from '../../containers/device-connection-modal.jsx';

import styles from './robot-manager-modal.css';

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Quản lý Robot',
        description: 'Robot manager modal title',
        id: 'gen.robot.modal.title'
    },
    colImage: {
        defaultMessage: 'Ảnh',
        description: 'Robot table column — image',
        id: 'gen.robot.table.col.image'
    },
    colName: {
        defaultMessage: 'Tên',
        description: 'Robot table column — name',
        id: 'gen.robot.table.col.name'
    },
    colType: {
        defaultMessage: 'Loại',
        description: 'Robot table column — type',
        id: 'gen.robot.table.col.type'
    },
    colFw: {
        defaultMessage: 'Firmware',
        description: 'Robot table column — firmware',
        id: 'gen.robot.table.col.firmware'
    },
    colStatus: {
        defaultMessage: 'Trạng thái',
        description: 'Robot table column — status',
        id: 'gen.robot.table.col.status'
    },
    colActions: {
        defaultMessage: 'Thao tác',
        description: 'Robot table column — actions',
        id: 'gen.robot.table.col.actions'
    },
    addRobot: {
        defaultMessage: '+ Thêm Robot',
        description: 'Add robot button',
        id: 'gen.robot.action.add'
    },
    edit: {
        defaultMessage: 'Sửa',
        description: 'Edit row tooltip',
        id: 'gen.robot.action.edit'
    },
    del: {
        defaultMessage: 'Xoá',
        description: 'Delete row tooltip',
        id: 'gen.robot.action.delete'
    },
    empty: {
        defaultMessage: 'Chưa có robot nào — bấm "+ Thêm Robot" để bắt đầu',
        description: 'Empty state',
        id: 'gen.robot.empty'
    },
    statusReady: {
        defaultMessage: 'Sẵn sàng',
        description: 'Status badge — ready',
        id: 'gen.robot.badge.ready'
    },
    statusOutdated: {
        defaultMessage: 'Cần cập nhật',
        description: 'Status badge — outdated',
        id: 'gen.robot.badge.outdated'
    },
    statusBeta: {
        defaultMessage: 'Bản thử nghiệm',
        description: 'Status badge — beta',
        id: 'gen.robot.badge.beta'
    },
    editingTitle: {
        defaultMessage: 'Chỉnh sửa Robot',
        description: 'Edit form title',
        id: 'gen.robot.form.title.edit'
    },
    creatingTitle: {
        defaultMessage: 'Thêm Robot mới',
        description: 'Add form title',
        id: 'gen.robot.form.title.create'
    },
    searchPlaceholder: {
        defaultMessage: 'Tìm kiếm robot theo tên...',
        description: 'Robot search box placeholder (UC-03 S-2)',
        id: 'gen.robot.search.placeholder'
    },
    searchLabel: {
        defaultMessage: 'Tìm kiếm robot',
        description: 'Robot search box aria-label',
        id: 'gen.robot.search.label'
    },
    searchEmpty: {
        defaultMessage: 'Không tìm thấy robot phù hợp',
        description: 'Empty state when search yields no robots (UC-03 S-2)',
        id: 'gen.robot.search.empty'
    },
    connect: {
        defaultMessage: 'Kết nối',
        description: 'Connect row action (UC-03 S-4)',
        id: 'gen.robot.action.connect'
    }
});

const STATUS_VARIANT = {ready: 'success', outdated: 'warning', beta: 'info'};

class RobotManagerModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['renderImageCell', 'renderStatusCell', 'renderActionsCell']);
    }
    renderImageCell (row) {
        return (
            <div
                className={styles.avatarCell}
                title={row.brand}
            >
                {row.name.substring(0, 2).toUpperCase()}
            </div>
        );
    }
    renderStatusCell (row) {
        const {intl} = this.props;
        const variant = STATUS_VARIANT[row.connectionStatus] || 'primary';
        let label = row.connectionStatus;
        if (row.connectionStatus === 'ready') label = intl.formatMessage(messages.statusReady);
        else if (row.connectionStatus === 'outdated') label = intl.formatMessage(messages.statusOutdated);
        else if (row.connectionStatus === 'beta') label = intl.formatMessage(messages.statusBeta);
        return (
            <MstemBadge variant={variant}>
                {label}
            </MstemBadge>
        );
    }
    renderActionsCell (row) {
        const {intl} = this.props;
        return (
            <RobotActionsCell
                connectLabel={intl.formatMessage(messages.connect)}
                deleteLabel={intl.formatMessage(messages.del)}
                editLabel={intl.formatMessage(messages.edit)}
                row={row}
                onConnect={this.props.onClickConnect}
                onDelete={this.props.onClickDelete}
                onEdit={this.props.onClickEdit}
            />
        );
    }
    buildColumns () {
        const {intl} = this.props;
        return [
            {
                key: 'image',
                label: intl.formatMessage(messages.colImage),
                width: '64px',
                render: this.renderImageCell
            },
            {key: 'name', label: intl.formatMessage(messages.colName)},
            {key: 'type', label: intl.formatMessage(messages.colType)},
            {key: 'firmwareVersion', label: intl.formatMessage(messages.colFw), width: '140px'},
            {
                key: 'connectionStatus',
                label: intl.formatMessage(messages.colStatus),
                width: '140px',
                render: this.renderStatusCell
            },
            {
                key: 'actions',
                label: intl.formatMessage(messages.colActions),
                width: '120px',
                render: this.renderActionsCell
            }
        ];
    }
    render () {
        const {
            robots,
            connectingRobot,
            editingRobot,
            isCreating,
            intl,
            searchTerm,
            onCancelForm,
            onClickAdd,
            onCloseConnect,
            onRequestClose,
            onSearch,
            onSubmitForm
        } = this.props;
        const inEditMode = isCreating || !!editingRobot;

        // UC-03 S-2: filter the list by name, case-insensitive.
        const term = (searchTerm || '').trim().toLowerCase();
        const filtered = term ?
            robots.filter(r => r.name.toLowerCase().includes(term)) :
            robots;
        const emptyMessage = term ?
            intl.formatMessage(messages.searchEmpty) :
            intl.formatMessage(messages.empty);

        if (connectingRobot) {
            return (
                <DeviceConnectionModal
                    deviceName={connectingRobot.name}
                    deviceType="robot"
                    onClose={onCloseConnect}
                />
            );
        }

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.modalTitle)}
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    {inEditMode ? (
                        <div>
                            <h3 className={styles.formTitle}>
                                {isCreating ?
                                    <FormattedMessage {...messages.creatingTitle} /> :
                                    <FormattedMessage {...messages.editingTitle} />}
                            </h3>
                            <RobotEditForm
                                initialRobot={editingRobot}
                                onCancel={onCancelForm}
                                onSave={onSubmitForm}
                            />
                        </div>
                    ) : (
                        <div>
                            <div className={styles.toolbar}>
                                <input
                                    aria-label={intl.formatMessage(messages.searchLabel)}
                                    className={styles.searchInput}
                                    placeholder={intl.formatMessage(messages.searchPlaceholder)}
                                    type="search"
                                    value={searchTerm}
                                    onChange={onSearch}
                                />
                                <button
                                    className={styles.btnPrimary}
                                    type="button"
                                    onClick={onClickAdd}
                                >
                                    <FormattedMessage {...messages.addRobot} />
                                </button>
                            </div>
                            <MstemTable
                                columns={this.buildColumns()}
                                data={filtered}
                                emptyMessage={emptyMessage}
                            />
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

RobotManagerModalComponent.propTypes = {
    connectingRobot: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
    editingRobot: PropTypes.shape({id: PropTypes.string}),
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    onCancelForm: PropTypes.func.isRequired,
    onClickAdd: PropTypes.func.isRequired,
    onClickConnect: PropTypes.func.isRequired,
    onClickDelete: PropTypes.func.isRequired,
    onClickEdit: PropTypes.func.isRequired,
    onCloseConnect: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    onSubmitForm: PropTypes.func.isRequired,
    robots: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    searchTerm: PropTypes.string
};

export default injectIntl(RobotManagerModalComponent);
